import { useEffect, useRef, useState } from 'react';
import { Avatar, Button, InlineError, Skeleton } from '../../components/ui';
import { usePasteImageUpload } from './pasteImage';
import { ReconnectDriveButton } from '../auth/ReconnectDrive';
import { activitySentence } from './projectActivity';
import type { ProjectActivityRow } from './projectActivity';
import { useProjectActivity } from './hooks';
import { isRichTextEmpty, toRichHtml } from '../../components/RichText';
import { CommentEditor } from './CommentEditor';
import { personMentionsIn } from './commentHtml';
import { missedMentionHandles, missedMentionNotice } from './missedMentions';
import { useCgAccess } from '../cgOrders/hooks';
import { useAuth } from '../auth/AuthProvider';
import { useTapPeople } from '../projects/hooks';
import { CommentBody } from './CommentBody';
import { useCreateComment, useDeleteComment, useTaskComments, useUpdateComment } from './hooks';
import { commentErrorMessage } from './queries';
import { COMMENT_BODY_MAX_LENGTH } from './types';
import { CommentReactions } from './CommentReactions';
import { useCommentReactions, useToggleReaction } from './reactionHooks';
import { mayReact, reactionErrorMessage } from './reactions';
import type { ReactionSummary } from './reactions';
import type { ProjectMemberWithProfile } from '../projects/types';
import type { CommentWithAuthor } from './types';
import type { MentionPerson } from './commentMentions';
import type { MentionSources } from '../notes/noteMentions';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * The comment thread (task panel and project discussion): one flat
 * chronological conversation, no nesting. Anyone who can edit the project
 * can comment; the author alone edits their comment (the database stamps the
 * edited indicator); the author or a project manager removes one.
 *
 * Bodies are rich text: the same bold/italic/lists/link toolbar as
 * descriptions, and pasted content from Docs, Slack, or an AI chat is
 * normalized to the app's own formatting on the way in.
 *
 * @mentions: typing @ offers every active TAP person, not just members —
 * mentioning a non-member is allowed, grants nothing, and quietly notifies
 * nobody (handoff 8), so the composer says so and points at the remedy: the
 * project owner adding them. Who is actually told is decided entirely by the
 * database triggers.
 *
 * Nothing here decides permission. canComment and canManage hide controls as
 * a convenience; Row Level Security refuses anything the caller may not do.
 */

/** When the comment was said. Time matters in a conversation, so it stays. */
export function formatCommentTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The stored body as display HTML with each known @name marked. Purely
 * visual: the database rows in comment_mentions are the truth, and an
 * unmatched name renders as plain text. Marking happens AFTER sanitizing,
 * on text nodes only, so nothing unsafe can ride in on a label.
 */
export function renderCommentHtml(body: string, mentionLabels: string[]): string {
  // Bodies come from two eras and only one of them needs help.
  //
  // The composer now writes HTML whose mentions are ELEMENTS — <mark> for a
  // person, <span data-type="mention"> for a record — so there is nothing to
  // find. Older bodies are plain text or the old rich subset, where a mention
  // is the characters "@Dana" and marking them is the only way the highlight
  // appears at all.
  //
  // toRichHtml is deliberately NOT used on an HTML body any more.
  // sanitizeRichText, which it calls, allows neither <mark> nor <span> nor
  // data-* attributes: running a new comment through it would silently delete
  // every mention pill in the thread. CommentBody sanitizes what comes back
  // here with DOMPurify, which is the door these tags are allowed through.
  const html = isMarkup(body) ? body : toRichHtml(body);
  const labels = mentionLabels.filter((label) => label.trim() !== '');
  if (labels.length === 0) {
    return html;
  }
  const escaped = labels
    .sort((a, b) => b.length - a.length)
    .map((label) => label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`@(?:${escaped.join('|')})`, 'g');

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Text;
    // Text already inside a mention is a mention that was stored as an
    // element. Marking it again nests <mark> inside <mark>, which reads as a
    // double highlight and, worse, buries the data-id one level deeper than
    // personMentionsIn looks.
    if (node.parentElement?.closest('mark.comment-mention, span[data-type="mention"]') != null) {
      continue;
    }
    textNodes.push(node);
  }
  for (const node of textNodes) {
    const text = node.textContent ?? '';
    if (!pattern.test(text)) {
      pattern.lastIndex = 0;
      continue;
    }
    pattern.lastIndex = 0;
    const fragment = doc.createDocumentFragment();
    let cursor = 0;
    for (const match of text.matchAll(pattern)) {
      const index = match.index ?? 0;
      if (index > cursor) {
        fragment.appendChild(doc.createTextNode(text.slice(cursor, index)));
      }
      const mark = doc.createElement('mark');
      mark.className = 'comment-mention';
      mark.textContent = match[0];
      fragment.appendChild(mark);
      cursor = index + match[0].length;
    }
    if (cursor < text.length) {
      fragment.appendChild(doc.createTextNode(text.slice(cursor)));
    }
    node.replaceWith(fragment);
  }
  return doc.body.innerHTML;
}

/** Whether a stored body is HTML rather than plain text. */
function isMarkup(body: string): boolean {
  return /<[a-z][^>]*>/i.test(body);
}

/**
 * A system line: what the database recorded, said plainly, sitting in the
 * thread rather than in a tab of its own. Deliberately quiet — it is context
 * for the conversation, not a participant in it.
 */
function ActivityRow({ text, when }: { text: string; when: string }) {
  return (
    <li className="activity-row">
      <span className="activity-dot" aria-hidden="true" />
      <span className="activity-text">{text}</span>
      <span className="activity-time">{when}</span>
    </li>
  );
}

function CommentRow({
  comment,
  mentionLabels,
  isOwn,
  canComment,
  canManage,
  onSaveEdit,
  onDelete,
  pending,
  highlighted = false,
  pasteImage,
  client,
  sources,
  people,
  reactions,
}: {
  comment: CommentWithAuthor;
  mentionLabels: string[];
  isOwn: boolean;
  canComment: boolean;
  canManage: boolean;
  onSaveEdit: (commentId: string, body: string, mentionedUserIds: string[]) => void;
  onDelete: (commentId: string) => void;
  pending: boolean;
  /** Arrived at by deep link: scroll to it and mark it for a beat. */
  highlighted?: boolean;
  pasteImage: (file: File) => Promise<string>;
  client: SupabaseClient;
  sources: MentionSources;
  people: MentionPerson[];
  /**
   * The quick reactions row (Drop 3b-2), or null where reactions do not apply
   * (deal and social-post threads: the database has no project to check).
   */
  reactions: {
    list: ReactionSummary[] | undefined;
    canReact: boolean;
    pending: boolean;
    onToggle: (commentId: string, emoji: string, mine: boolean) => void;
  } | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  // A pasted screenshot still uploading into the edit box: Save waits for it.
  const [uploading, setUploading] = useState(false);
  const rowRef = useRef<HTMLLIElement | null>(null);

  const authorName = comment.author_name === '' ? 'Someone' : comment.author_name;
  const mayEdit = isOwn && canComment;
  const mayDelete = (isOwn && canComment) || canManage;

  /**
   * A comment arrived at by deep link scrolls itself into view once. Anchoring
   * happens here rather than at the section, because only the row knows when
   * it has actually rendered — the list is async and an id lookup from above
   * would fire against an empty thread.
   */
  useEffect(() => {
    if (!highlighted) {
      return;
    }
    rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlighted]);

  function startEdit() {
    setDraft(comment.body);
    setEditing(true);
  }

  function commitEdit() {
    if (uploading) {
      return;
    }
    if (isRichTextEmpty(draft) || draft === comment.body) {
      setEditing(false);
      return;
    }
    // Read out of the edited text, not out of what was picked when the
    // comment was first written. A mention added here used to render a pill
    // and notify nobody, because only the body was ever sent.
    onSaveEdit(
      comment.id,
      draft,
      personMentionsIn(draft).map((person) => person.id),
    );
    setEditing(false);
  }

  return (
    <li
      ref={rowRef}
      id={`comment-${comment.id}`}
      className={`comment-row${highlighted ? ' comment-row--highlighted' : ''}`}
    >
      {/* The author's picture, as a column the name line and the body both
          sit beside. Kyu picked this over a smaller picture on the name line
          (17 September, "Comment rows, three ways") knowing it spends 36px of
          body width in the 385px discussion column. Members size, 28px, so
          the same face is the same size here and on the Members card. */}
      <Avatar name={authorName} imageUrl={comment.author_avatar_url} size="sm" />
      {/* Author, time, and — on the same line, hard right — the actions.
          Kyu, 3 September: Edit and Delete belong beside the author line, not
          under the comment. He is right about what was wrong: under the body
          they read as the last line of what somebody wrote, and they cost 34px
          of height per comment in a column that is mostly comments. */}
      <div className="comment-meta">
        <span className="comment-author">{authorName}</span>
        <span className="comment-date">
          {formatCommentTime(comment.created_at)}
          {comment.edited_at === null ? '' : ' (edited)'}
        </span>
        {editing || (!mayEdit && !mayDelete) ? null : (
          <div className="comment-actions">
            {mayEdit ? (
              <Button variant="ghost" size="sm" onClick={startEdit}>
                Edit
              </Button>
            ) : null}
            {mayDelete ? (
              confirming ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                    Keep
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      setConfirming(false);
                      onDelete(comment.id);
                    }}
                    disabled={pending}
                  >
                    Confirm delete
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Delete comment by ${authorName}`}
                  onClick={() => setConfirming(true)}
                >
                  Delete
                </Button>
              )
            ) : null}
          </div>
        )}
      </div>

      {editing ? (
        <div className="comment-edit">
          {/* The same editor as the composer, deliberately. Editing through
              the old one would put the body back through sanitizeRichText,
              which allows neither <mark> nor <span> — so saving a typo fix
              would silently delete every mention in the comment and, with
              them, nothing visible to explain why the highlights vanished. */}
          <CommentEditor
            label={`Edit comment by ${authorName}`}
            value={draft}
            onChange={setDraft}
            onSubmit={commitEdit}
            client={client}
            sources={sources}
            people={people}
            onPasteImage={pasteImage}
            onUploadingChange={setUploading}
          />
          {/* The edit box needs this as much as the composer does. Retyping a
              name by hand while fixing a typo is one of the ways a mention
              stops being a mention, and it is the way that happens to somebody
              who already knows how the feature works. */}
          {missedMentionNotice(missedMentionHandles(draft, people)) === '' ? null : (
            <p className="comment-missed-mention-note">
              {missedMentionNotice(missedMentionHandles(draft, people))}
            </p>
          )}
          <div className="comment-actions">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={commitEdit} disabled={pending || uploading}>
              {pending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        // Rendered statically — no editor is instantiated for a posted
        // comment. renderCommentHtml still does the @person marking that the
        // notification pipeline depends on; CommentBody then puts the result
        // through DOMPurify and makes entity mention pills clickable.
        <CommentBody html={renderCommentHtml(comment.body, mentionLabels)} />
      )}

      {editing || reactions === null ? null : (
        <CommentReactions
          reactions={reactions.list}
          canReact={reactions.canReact}
          pending={reactions.pending}
          onToggle={(emoji, mine) => reactions.onToggle(comment.id, emoji, mine)}
        />
      )}
    </li>
  );
}

export function CommentsSection({
  taskId,
  projectId,
  dealId = null,
  socialPostId = null,
  canComment,
  canManage,
  members,
  highlightCommentId = null,
  heading = 'Comments',
}: {
  /** Null addresses the project-level discussion thread. */
  taskId: string | null;
  projectId: string;
  /** Set instead of a project when the thread belongs to a deal. */
  dealId?: string | null;
  /** Set instead of a project when the thread belongs to a social post. */
  socialPostId?: string | null;
  /** May comment: the same people who may edit the project. */
  canComment: boolean;
  /** May remove anyone's comment: TAP Admin or the project owner. */
  canManage: boolean;
  members: ProjectMemberWithProfile[];
  /** Comment to scroll to and mark, when arrived at from an Inbox deep link. */
  highlightCommentId?: string | null;
  /**
   * Section title. Pass an empty string when a surrounding Panel already
   * carries the title (the project page's Discussion panel) so the name is
   * not said twice.
   */
  heading?: string;
}) {
  const pasteImage = usePasteImageUpload();
  const { access, client } = useAuth();
  // CG orders are gated, so a non-member's query returns nothing. Asking
  // anyway would be a round trip per keystroke for an empty list.
  const cgAccess = useCgAccess();
  const currentUserId = access.status === 'approved' ? access.profile.id : '';

  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  // Set when a pasted image failed only because Drive needs reconnecting. The
  // remedy is one click, so the button belongs here rather than in a sentence
  // pointing at a menu.
  const [needsReconnect, setNeedsReconnect] = useState(false);
  // A pasted screenshot still uploading: Comment waits for it (design note S16).
  const [uploading, setUploading] = useState(false);

  const comments = useTaskComments(taskId, projectId, dealId, socialPostId);
  // Only the project thread carries project history; a task drawer has its
  // own activity list and this scope would be the wrong one there.
  const activity = useProjectActivity(
    projectId,
    taskId === null && dealId === null && socialPostId === null,
  );
  const people = useTapPeople(canComment);
  const createComment = useCreateComment(taskId, projectId, dealId, socialPostId);
  const updateComment = useUpdateComment(taskId, projectId);
  const deleteComment = useDeleteComment(taskId, projectId);

  // Quick reactions (Drop 3b-2) on every thread this section shows: project,
  // deal and social post. The database decides who may see and add them
  // (comment_in_visible_project, 20270181000100, given its deal and
  // social-post branches in 20270215000100), with the same test as reading
  // the comment itself.
  const commentIds = (comments.data ?? []).map((comment) => comment.id);
  const reactionList = useCommentReactions(commentIds);
  const toggleReaction = useToggleReaction();
  const canReact = mayReact(access.status === 'approved' ? access.profile : null);
  const reactionsFor = (commentId: string) => ({
    list: reactionList.data?.get(commentId),
    canReact,
    pending: toggleReaction.isPending,
    onToggle: (id: string, emoji: string, mine: boolean) => {
      setError('');
      toggleReaction.mutate(
        { commentId: id, emoji, mine },
        {
          onError: (cause) => {
            const message = reactionErrorMessage(cause instanceof Error ? cause.message : '');
            if (message !== '') {
              setError(message);
            }
          },
        },
      );
    },
  });

  const everyone = (people.data ?? []).map((person) => ({
    id: person.id,
    label: person.display_name.trim() !== '' ? person.display_name : person.email,
  }));

  const labelById = new Map(everyone.map((person) => [person.id, person.label]));
  for (const member of members) {
    if (!labelById.has(member.user_id)) {
      labelById.set(
        member.user_id,
        member.display_name.trim() !== '' ? member.display_name : member.email,
      );
    }
  }

  // Names for the system lines resolve from the same lookup the mentions use,
  // so a renamed person reads correctly in months-old history.
  const nameById = labelById;

  /**
   * Comments and system lines share one chronological thread. They are merged
   * here rather than shown in two stacks because the whole point of dropping
   * the Activity tab was that a status change is context for the comment that
   * follows it.
   */
  const timeline: (
    | { kind: 'comment'; at: string; comment: CommentWithAuthor }
    | { kind: 'activity'; at: string; row: ProjectActivityRow }
  )[] = [
    ...(comments.data ?? []).map(
      (comment) => ({ kind: 'comment', at: comment.created_at, comment }) as const,
    ),
    ...(activity.data ?? []).map((row) => ({ kind: 'activity', at: row.created_at, row }) as const),
    // Newest first: the reply somebody is looking for is almost always the
    // last one, and on a long thread oldest-first buries it below a scroll.
    // Activity rows interleave by the same clock, so the order stays honest.
  ].sort((a, b) => b.at.localeCompare(a.at));

  /**
   * Who is actually named in the box right now.
   *
   * Read out of the body's own markup rather than from a list kept alongside
   * it. The old composer tracked what had been PICKED and then checked whether
   * the text still contained "@Name" — which meant editing the name by hand
   * left a notification aimed at somebody the comment no longer mentions, and
   * deleting the pill but leaving the letters kept the notification alive.
   * A mention is now an element: it is either in the document or it is not.
   */
  const activeMentions = personMentionsIn(body);
  const memberIds = new Set(members.map((member) => member.user_id));
  const outsiders = activeMentions.filter((person) => !memberIds.has(person.id));
  // A name typed at somebody that never became a mention. Checked as they
  // type rather than on submit: the moment it can still be put right is
  // before the button, and a notice that appears after posting is a report.
  const missedNotice = missedMentionNotice(missedMentionHandles(body, everyone));

  function report(cause: unknown) {
    setError(commentErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function submit() {
    if (uploading) {
      return;
    }
    // An image-only comment has no text at all. Treating it as empty would
    // refuse to post a screenshot, which is a normal thing to send.
    if (isRichTextEmpty(body) && !body.includes('<img')) {
      setError('Write a comment first.');
      return;
    }
    if (body.length > COMMENT_BODY_MAX_LENGTH) {
      setError('That comment is too long. Split it into two.');
      return;
    }
    setError('');
    createComment.mutate(
      {
        body,
        mentionedUserIds: activeMentions.map((person) => person.id),
      },
      {
        onSuccess: () => {
          setBody('');
        },
        onError: report,
      },
    );
  }

  function saveEdit(commentId: string, nextBody: string, mentionedUserIds: string[]) {
    setError('');
    updateComment.mutate(
      {
        commentId,
        body: nextBody,
        mentionedUserIds,
        dealId: dealId ?? null,
        socialPostId: socialPostId ?? null,
      },
      { onError: report },
    );
  }

  function remove(commentId: string) {
    setError('');
    deleteComment.mutate(commentId, { onError: report });
  }

  return (
    <section className="comments-section" aria-label={heading === '' ? 'Discussion' : heading}>
      {heading === '' ? null : <h3 className="task-panel-heading">{heading}</h3>}

      {error === '' ? null : <InlineError>{error}</InlineError>}

      {/* The box sits above the thread: replying is the reason people open
          this, and putting it under a long thread makes the common action the
          one you have to scroll for. */}
      {canComment ? (
        <div className="comment-composer">
          <CommentEditor
            label="Add a comment"
            value={body}
            onChange={setBody}
            onSubmit={submit}
            client={client}
            sources={{ cgMember: cgAccess.data === true }}
            people={everyone}
            onPasteImage={pasteImage}
            onUploadingChange={setUploading}
            onPasteError={(cause) => {
              const message = cause instanceof Error ? cause.message : '';
              // Only this one failure has a one-click remedy, so only this one
              // gets a button; everything else is said in words.
              setNeedsReconnect(message.toLowerCase().includes('reconnect'));
              setError(message === '' ? 'That image could not be uploaded.' : message);
            }}
          />

          {outsiders.length === 0 ? null : (
            <p className="comment-outsider-note">
              {/* This used to end "and sends nothing". That stopped being true
                  when 20261205000100 shipped Kyu's rule — "@mention = notify
                  the person. Full stop." The notification now goes out
                  whatever their membership, stamped so the Inbox can say why
                  the link will not open. Telling somebody their mention did
                  nothing, at the moment it did something, is the same failure
                  as the missed mention below pointing the other way. */}
              {outsiders.map((person) => person.label).join(', ')}{' '}
              {outsiders.length === 1 ? 'is' : 'are'} not in this project.{' '}
              {outsiders.length === 1 ? 'They will' : 'They will each'} still be notified, but the
              link will not open for {outsiders.length === 1 ? 'them' : 'them'} — ask the project
              owner to add {outsiders.length === 1 ? 'them' : 'them'} if they need to see it.
            </p>
          )}

          {missedNotice === '' ? null : (
            <p className="comment-missed-mention-note">{missedNotice}</p>
          )}

          {needsReconnect ? (
            <div className="comment-reconnect">
              <ReconnectDriveButton />
              <span className="drive-connection-note">
                Then paste the image again — the comment you have typed stays put.
              </span>
            </div>
          ) : null}

          <div className="comment-actions">
            <Button onClick={submit} disabled={createComment.isPending || uploading}>
              {createComment.isPending ? 'Posting…' : 'Comment'}
            </Button>
          </div>
        </div>
      ) : null}

      {comments.isPending ? (
        <Skeleton lines={2} label="Loading comments" />
      ) : comments.isError ? (
        <p className="task-panel-text">Comments could not be loaded.</p>
      ) : timeline.length === 0 ? (
        <p className="task-panel-text">No comments yet</p>
      ) : (
        <ul className="comment-list">
          {timeline.map((entry) =>
            entry.kind === 'comment' ? (
              <CommentRow
                key={entry.comment.id}
                client={client}
                sources={{ cgMember: cgAccess.data === true }}
                people={everyone}
                comment={entry.comment}
                mentionLabels={entry.comment.mentioned_user_ids
                  .map((id) => labelById.get(id) ?? '')
                  .filter((label) => label !== '')}
                isOwn={entry.comment.author_id === currentUserId}
                canComment={canComment}
                canManage={canManage}
                onSaveEdit={saveEdit}
                onDelete={remove}
                pending={updateComment.isPending || deleteComment.isPending}
                highlighted={entry.comment.id === highlightCommentId}
                pasteImage={pasteImage}
                reactions={reactionsFor(entry.comment.id)}
              />
            ) : (
              <ActivityRow
                key={entry.row.id}
                text={`${entry.row.actor_name} ${activitySentence(entry.row, nameById)}`}
                when={formatCommentTime(entry.row.created_at)}
              />
            ),
          )}
        </ul>
      )}
    </section>
  );
}
